import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, MenuItem, Switch, TextField, Typography, Box, Chip
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import toast from "react-hot-toast";

const API = process.env.REACT_APP_API_URL;
const EMPTY_FORM = { note: "", category: "general", priority: "medium", visibility: "internal", is_active: true };

const BedSeatNotesModal = ({ open, onClose, item, type, onChanged }) => {
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);

  const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  const resource = type === "bed" ? "hostel/bed-notes" : "library/seat-notes";
  const foreignKey = type === "bed" ? "bed" : "seat";

  const fetchNotes = async () => {
    if (!open || !item?.id) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}${resource}/?${foreignKey}_id=${item.id}`, config());
      setNotes(Array.isArray(response.data) ? response.data : response.data.results || []);
    } catch (error) {
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
    if (!open) {
      setForm(EMPTY_FORM);
      setEditingId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id, type]);

  const saveNote = async () => {
    if (!form.note.trim()) {
      toast.error("Note text is required");
      return;
    }
    try {
      if (editingId) {
        await axios.patch(`${API}${resource}/${editingId}/`, form, config());
        toast.success("Note updated successfully");
      } else {
        await axios.post(`${API}${resource}/`, { ...form, [foreignKey]: item.id }, config());
        toast.success("Note created successfully");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      await fetchNotes();
      await onChanged?.();
    } catch (error) {
      const message = error.response?.data?.note?.[0] || error.response?.data?.detail || "Unable to save note";
      toast.error(message);
    }
  };

  const editNote = (note) => {
    setEditingId(note.id);
    setForm({ note: note.note, category: note.category, priority: note.priority, visibility: note.visibility || "internal", is_active: note.is_active });
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}${resource}/${noteId}/`, config());
      toast.success("Note deleted successfully");
      await fetchNotes();
      await onChanged?.();
    } catch (error) {
      toast.error("Unable to delete note");
    }
  };

  const identifier = type === "bed"
    ? `Room ${item?.room?.room_number || "-"} / Bed ${item?.bed_number || "-"}`
    : `Seat ${item?.seat_number || "-"}`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Notes - {identifier}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "grid", gap: 2, mb: 3 }}>
          <TextField label="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} multiline rows={3} inputProps={{ maxLength: 2000 }} required />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <TextField select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {["general", "maintenance", "cleaning", "allocation", "student_request", "safety", "admin_instruction", "other"].map((value) => <MenuItem key={value} value={value}>{value.replaceAll("_", " ")}</MenuItem>)}
            </TextField>
            <TextField select label="Priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {["low", "medium", "high", "critical"].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
          </Box>
          <TextField select label="Visibility" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })}>
            <MenuItem value="internal">Internal - Admin Only</MenuItem>
            <MenuItem value="student">Student Visible</MenuItem>
          </TextField>
          <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />} label="Active note" />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={saveNote}>{editingId ? "Update Note" : "Add Note"}</Button>
            {editingId && <Button onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}>Cancel Edit</Button>}
          </Box>
        </Box>

        <Typography variant="h6" sx={{ mb: 1 }}>Note History ({notes.length})</Typography>
        {loading ? <Typography>Loading notes...</Typography> : notes.length === 0 ? <Typography color="text.secondary">No notes added.</Typography> : notes.map((note) => (
          <Box key={note.id} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2, mb: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Box>
                <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                  <Chip size="small" label={note.category.replaceAll("_", " ")} />
                  <Chip size="small" label={note.priority} color={note.priority === "critical" || note.priority === "high" ? "error" : "default"} />
                  <Chip size="small" label={note.visibility === "student" ? "Student Visible" : "Internal"} color={note.visibility === "student" ? "primary" : "default"} />
                  {!note.is_active && <Chip size="small" label="Inactive" />}
                </Box>
                <Typography sx={{ whiteSpace: "pre-wrap" }}>{note.note}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Created by {note.created_by_name || "Admin"} on {new Date(note.created_at).toLocaleString()}
                  {note.updated_at !== note.created_at ? ` • Updated ${new Date(note.updated_at).toLocaleString()}` : ""}
                </Typography>
              </Box>
              <Box sx={{ whiteSpace: "nowrap" }}>
                <IconButton size="small" onClick={() => editNote(note)}><Edit fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => deleteNote(note.id)}><Delete fontSize="small" /></IconButton>
              </Box>
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

export default BedSeatNotesModal;
